// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getDataView, getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import GridContainer, { GridContainerProps } from "@store/MobiusOverrides/GridContainer/GridContainer";
import GridItem from "@store/MobiusOverrides/GridItem/GridItem";
import Img, { ImgProps } from "@store/MobiusOverrides/Img/Img";
import Link from "@store/MobiusOverrides/Link/Link";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { loadPagesForParent } from "@store/Framework/Store/Data/Pages/PagesActionCreators";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import sortBy from "lodash/sortBy";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    itemsPerRow = "itemsPerRow",
    showImage = "showImage",
    placeholderImage = "placeholderImage",
    ctaType = "ctaType",
    ctaLabel = "ctaLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.itemsPerRow]: number;
        [fields.showImage]: boolean;
        [fields.placeholderImage]: string;
        [fields.ctaType]: string;
        [fields.ctaLabel]: string;
    };
}

const mapStateToProps = (state: Toolkit.Store.ApplicationState) => {
    const currentPage = getCurrentPage(state);
    const pagesDataView = getDataView(state.data.pages, {
        parentNodeId: currentPage.nodeId,
    });
    const pages = {
        ...pagesDataView,
        value: pagesDataView.value?.filter(x => x.fields["excludeFromNavigation"] === false),
    };
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }

    return {
        links,
        pages,
        currentPage,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
};

type Props = OwnProps &
    WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

export interface SubPagesListStyles {
    wrapper: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    container?: GridContainerProps;
    pageItem?: GridItemProps;
    noSubPageText?: TypographyPresentationProps;
    pageCard?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: ImgProps;
    pageTitle?: TypographyPresentationProps;
    cardLink?: LinkPresentationProps;
    cardButtonPrimary?: ButtonPresentationProps;
    cardButtonSecondary?: ButtonPresentationProps;
}

export const subPagesListStyles: SubPagesListStyles = {
    wrapper: {
        css: css`
            ${spacingStyles(pxToRem(50), pxToRem(50))}
        `,
    },
    container: {
        gap: 30,
        css: css`
            width: 100%;
        `,
        offsetCss: css`
            gap: ${pxToRem(70, 0)};
        `,
    },
    pageItem: {
        css: css`
            width: 100%;
        `,
    },
    noSubPageText: {
        css: css`
            padding: ${pxToRem(5)};
            background-color: ${getColor("common.accent")};
        `,
    },
    pageCard: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: ${pxToRem(10)};
            height: 100%;
            justify-content: space-between;
        `,
    },
    imageWrapper: {
        css: css`
            position: relative;
            padding-top: ${(630 / 1200) * 100}%;
            border: 1px solid ${getColor("common.border")};
        `,
    },
    image: {
        css: css`
            position: absolute;
            top: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
        `,
    },
    pageTitle: {
        variant: "h3",
        forwardAs: "span",
        color: "primary",
    },
    cardLink: {
        color: "secondary",
        css: css`
            ${linkStyles("secondary", "text.linkHover", false, true)}
            align-self: flex-start;
            margin-top: auto;
        `,
    },
    cardButtonPrimary: {
        variant: "primary",
        css: css`
            ${breakpoint("sm")} {
                align-self: flex-start;
            }
            margin-top: auto;
        `,
    },
    cardButtonSecondary: {
        variant: "secondary",
        css: css`
            ${breakpoint("sm")} {
                align-self: flex-start;
            }
            margin-top: auto;
        `,
    },
};

const styles = subPagesListStyles;

const SubPagesDynamicList: FC<Props> = ({
    pages,
    currentPage,
    links,
    fields: { itemsPerRow, showImage, placeholderImage, ctaLabel, ctaType },
    loadPagesForParent,
}) => {
    useEffect(() => {
        if (pages.value || !pages.isLoading) {
            loadPagesForParent({
                parentNodeId: currentPage.nodeId,
            });
        }
    }, []);

    const { isInShell } = useShellContext();
    const history = useHistory();

    const goToUrl = (url: string) => {
        history.push(url);
    };

    if (!links && isInShell) {
        return (
            <Typography {...styles.noSubPageText}>
                There is no sub-page to display. <small>(This will not display on the storefront)</small>
            </Typography>
        );
    }

    const sortedAndPagedPages = sortBy(pages.value, o => o.fields["publishDate"] || "").reverse();

    return (
        <StyledWrapper {...styles.wrapper}>
            <LoadingOverlay loading={pages.isLoading || !pages.value} {...styles.loadingOverlay}>
                <GridContainer {...styles.container}>
                    {sortedAndPagedPages.map((page: PageProps) => (
                        <GridItem
                            width={[12, 12, itemsPerRow, itemsPerRow, itemsPerRow] as GridWidths[]}
                            key={page.id}
                            {...styles.pageItem}
                        >
                            <StyledWrapper {...styles.pageCard}>
                                {showImage && (page.fields["openGraphImage"] || placeholderImage) && (
                                    <StyledWrapper {...styles.imageWrapper}>
                                        <Img
                                            src={page.fields["openGraphImage"] || placeholderImage}
                                            {...styles.image}
                                        />
                                    </StyledWrapper>
                                )}
                                <Typography {...styles.pageTitle}>{page.fields["title"]}</Typography>
                                {ctaType === "link" ? (
                                    <Link href={links[page.nodeId]?.url} {...styles.cardLink}>
                                        {ctaLabel}
                                    </Link>
                                ) : (
                                    <Button
                                        onClick={() => {
                                            goToUrl(`${links[page.nodeId]?.url}`);
                                        }}
                                        {...(ctaType === "buttonPrimary"
                                            ? styles.cardButtonPrimary
                                            : styles.cardButtonSecondary)}
                                    >
                                        {ctaLabel}
                                    </Button>
                                )}
                            </StyledWrapper>
                        </GridItem>
                    ))}
                </GridContainer>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(SubPagesDynamicList)),
    definition: {
        displayName: "Sub-Pages Dynamic List",
        group: "Common",
        icon: "grid",
        fieldDefinitions: [
            {
                name: fields.itemsPerRow,
                editorTemplate: "DropDownField",
                defaultValue: 4,
                fieldType: "General",
                options: [
                    {
                        displayName: "2",
                        value: 6,
                    },
                    {
                        displayName: "3",
                        value: 4,
                    },
                    {
                        displayName: "4",
                        value: 3,
                    },
                ],
            },
            {
                name: fields.showImage,
                displayName: "Show Page Image?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.placeholderImage,
                displayName: "Default image if page has no OpenGraph Image.",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.showImage === true,
            },
            {
                name: fields.ctaType,
                displayName: "Type of Call to Action for each page.",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Link",
                        value: "link",
                    },
                    {
                        displayName: "Button (Primary)",
                        value: "buttonPrimary",
                    },
                    {
                        displayName: "Button (Secondary)",
                        value: "buttonSecondary",
                    },
                ],
                defaultValue: "link",
                fieldType: "General",
            },
            {
                name: fields.ctaLabel,
                displayName: "Call to actions button label ",
                editorTemplate: "TextField",
                defaultValue: "Read More",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
